import { FieldValue, QueryField } from '../../../../../types/QueryBuilder';

export type Translate = (key: string, params?: Record<string, string | number>) => string;

const OTHER_CATEGORY_KEY = 'pages.clients.queryBuilder.simple.categories.other';
export const GDPR_CATEGORY_TITLE_KEY = 'pages.clients.queryBuilder.simple.categories.gdpr';
export const CLIENT_LISTS_CATEGORY_TITLE_KEY = 'pages.clients.queryBuilder.simple.categories.clientLists';

export const STORE_FIELD_NAME = 'PERSON_ENTITY';
export const PURCHASE_PERIOD_FIELD_NAME = 'TRANSACTION_PERIOD';
export const PURCHASE_COUNT_FIELD_NAME = 'TRANSACTION_NB';
export const COUNTER_FIELD_NAME = 'CUSTOMER_ACCOUNT';
export const COUNTER_TYPE_FIELD_NAME = 'CUSTOMER_ACCOUNT_TYPE';
export const IDENTIFIER_LIST_FIELD_NAME = 'CUSTOMER_IDENTIFIER';
const COUNTRY_FIELD_NAME = 'CUSTOMER_COUNTRY';

// BrandConfigContext keys (per-enseigne, fetched from the suiteoneconfig API) driving the
// CUSTOMER_IDENTIFIER dual-list: which identifier types are shown at all, and which of those start
// pre-checked. Mirrors the previous BeOne UI's `query_identifiers`/`default_query_identifiers`.
export const IDENTIFIERS_VISIBLE_LIST_CONFIG_KEY = 'query.identifiers.default.list';
export const IDENTIFIERS_DEFAULT_CHECKED_CONFIG_KEY = 'query.identifiers.default.checked';

export const parseConfigIdList = (raw: string | undefined): string[] | undefined =>
    raw ? raw.split(',').map(v => v.trim()).filter(Boolean) : undefined;

export const OPTIN_TO_STATUS_FIELD_NAME: Record<string, string> = {
    CUSTOMER_OPTIN_EMAIL: 'CUSTOMER_STATUS_EMAIL',
    CUSTOMER_OPTIN_SMS: 'CUSTOMER_STATUS_SMS',
    CUSTOMER_OPTIN_COURRIER: 'CUSTOMER_STATUS_COURRIER',
};

// TRANSACTION_NB no longer has its own card — the "Critères de comportement d'achat" section
// now drives it through a single Actifs/Inactifs select next to TRANSACTION_PERIOD (see
// PurchaseActivityRow in FieldRow.tsx), so it's merged the same way CUSTOMER_ACCOUNT_TYPE and the
// GDPR status fields are.
export const MERGED_INTO_FIELD_NAMES = new Set([
    COUNTER_TYPE_FIELD_NAME,
    PURCHASE_COUNT_FIELD_NAME,
    ...Object.values(OPTIN_TO_STATUS_FIELD_NAME),
]);

export const DIALOG_SELECT_FIELD_NAMES = new Set([STORE_FIELD_NAME, 'CUSTOMER_CITY', COUNTRY_FIELD_NAME, 'CUSTOMER_DEPARTMENT']);

export const DUAL_LIST_FIELD_NAMES = new Set(['CUSTOMER_IDENTIFIER', 'CUSTOMER_LIST_BY_IDENTIFIER', 'CUSTOMER_LIST_BY_CARTE']);

export const DUAL_LIST_NO_SEARCH_FIELD_NAMES = new Set(['CUSTOMER_IDENTIFIER']);

export const ROW_SPAN_2_FIELD_NAMES = new Set(['CUSTOMER_SCORE', STORE_FIELD_NAME]);

// Pins each "Catégorie de clients" field to a specific column/row on the 2-column grid, instead of
// leaving it to CSS Grid auto-placement: Magasins/Identifiants on row 1, Ouverture de la carte and
// Type d'abonnements together on row 2, Compteur fidélité on row 3 — the layout this section always
// had before auto-placement (interacting with PERSON_ENTITY's 2-row span) started reflowing it onto
// a single wide row instead.
export const FIELD_GRID_POSITION: Record<string, { column: number; row: number }> = {
    PERSON_ENTITY: { column: 1, row: 1 },
    CUSTOMER_IDENTIFIER: { column: 2, row: 1 },
    CUSTOMER_ACTIVATION_DATE: { column: 2, row: 2 },
    CUSTOMER_SUBSCRIPTIONS_TYPE: { column: 1, row: 3 },
    CUSTOMER_ACCOUNT: { column: 2, row: 3 },
};

const COUNTRY_LABEL_PREFIX_RE = /^\d+\s*-\s*/;
const COUNTRY_PRIORITY = ['FRANCE', 'BELGIQUE', 'LUXEMBOURG', 'ALLEMAGNE', 'SUISSE', 'ITALIE', 'ESPAGNE', 'MONACO', 'ANDORRE'];
const COUNTRY_PRIORITY_ORDER = new Map(COUNTRY_PRIORITY.map((name, index) => [name, index]));

const STATUS_ALLOWED_LABELS: Record<string, string[]> = {
    CUSTOMER_STATUS_EMAIL: ['VALID', 'SOFT1', 'SOFT2', 'HARD', 'NON RENSEIGNE'],
    CUSTOMER_STATUS_SMS: ['VALID', 'HARD', 'NON RENSEIGNE'],
    CUSTOMER_STATUS_COURRIER: ['VALID', 'ADRESSE INVALIDE', 'NPAI', 'NON RENSEIGNE'],
};

const STATUS_LABEL_KEYS: Record<string, string> = {
    VALID: 'valid',
    'NON RENSEIGNE': 'notSpecified',
    'ADRESSE INVALIDE': 'invalid',
    HARD: 'invalid',
};

const SOFTBOUNCE_NAMES = ['SOFT1', 'SOFT2'];
const SOFTBOUNCE_MERGED_NAME = SOFTBOUNCE_NAMES.join('_');

const FRENCH_MONTH_INDEX: Record<string, number> = {
    janvier: 0, février: 1, fevrier: 1, mars: 2, avril: 3, mai: 4, juin: 5,
    juillet: 6, août: 7, aout: 7, septembre: 8, octobre: 9, novembre: 10, décembre: 11, decembre: 11,
};
const LABEL_MONTH_YEAR_RE = new RegExp(`(${Object.keys(FRENCH_MONTH_INDEX).join('|')})\\D{0,3}(\\d{4})`, 'i');
const DATE_SORTED_FIELD_NAMES = new Set(['CUSTOMER_LIST_BY_IDENTIFIER', 'CUSTOMER_LIST_BY_CARTE']);

function extractLabelDateSortKey(label: string): number | null {
    const match = label.match(LABEL_MONTH_YEAR_RE);
    if (!match) return null;
    const monthIndex = FRENCH_MONTH_INDEX[match[1].toLowerCase()];
    const year = parseInt(match[2], 10);
    return monthIndex === undefined || Number.isNaN(year) ? null : year * 12 + monthIndex;
}

const LEADING_NUMBER_RE = /^0*(\d+)\s*[.)-]/;

function extractLeadingNumber(label: string): number | null {
    const match = label.match(LEADING_NUMBER_RE);
    return match ? parseInt(match[1], 10) : null;
}

function sortByLeadingNumber<T extends { label: string }>(items: T[]): T[] {
    return items
        .map((item, index) => ({ item, key: extractLeadingNumber(item.label), index }))
        .sort((a, b) => {
            if (a.key !== null && b.key !== null) return a.key - b.key;
            if (a.key !== null) return -1;
            if (b.key !== null) return 1;
            return a.index - b.index;
        })
        .map(({ item }) => item);
}

export function resolveFieldValues(field: QueryField, t: Translate): FieldValue[] | undefined {
    if (!field.values) return field.values;

    const usableValues = sortByLeadingNumber(field.values.filter(v => v.name && v.label.trim()));

    const statusAllowedLabels = STATUS_ALLOWED_LABELS[field.name];
    if (statusAllowedLabels) {
        const order = new Map(statusAllowedLabels.map((label, index) => [label, index]));
        const sortedValues = usableValues
            .filter(v => order.has(v.label.trim().toUpperCase()))
            .sort((a, b) => order.get(a.label.trim().toUpperCase())! - order.get(b.label.trim().toUpperCase())!)
            .map(v => {
                const overrideKey = STATUS_LABEL_KEYS[v.label.trim().toUpperCase()];
                return overrideKey ? { ...v, label: t(`pages.clients.queryBuilder.simple.status.${overrideKey}`) } : v;
            });

        const isSoftbounce = (v: FieldValue) => SOFTBOUNCE_NAMES.includes(v.label.trim().toUpperCase());
        const softbounceValues = sortedValues.filter(isSoftbounce);
        if (softbounceValues.length === 0) return sortedValues;

        const firstSoftbounceIndex = sortedValues.findIndex(isSoftbounce);
        const insertAt = sortedValues.slice(0, firstSoftbounceIndex).filter(v => !isSoftbounce(v)).length;
        const merged: FieldValue = {
            name: SOFTBOUNCE_MERGED_NAME,
            label: t('pages.clients.queryBuilder.simple.softbounce.label'),
            mergedNames: softbounceValues.map(v => v.name),
            tooltip: t('pages.clients.queryBuilder.simple.softbounce.tooltip'),
        };
        const withoutSoftbounce = sortedValues.filter(v => !isSoftbounce(v));
        withoutSoftbounce.splice(insertAt, 0, merged);
        return withoutSoftbounce;
    }

    if (DATE_SORTED_FIELD_NAMES.has(field.name)) {
        return usableValues
            .map((v, index) => ({ v, key: extractLabelDateSortKey(v.label), index }))
            .sort((a, b) => {
                if (a.key !== null && b.key !== null) return b.key - a.key;
                if (a.key !== null) return -1;
                if (b.key !== null) return 1;
                return a.index - b.index;
            })
            .map(({ v }) => v);
    }

    if (field.name !== COUNTRY_FIELD_NAME) return usableValues;

    return usableValues
        .map(v => ({ ...v, label: v.label.replace(COUNTRY_LABEL_PREFIX_RE, '') }))
        .sort((a, b) => {
            const orderA = COUNTRY_PRIORITY_ORDER.get(a.label) ?? Infinity;
            const orderB = COUNTRY_PRIORITY_ORDER.get(b.label) ?? Infinity;
            return orderA !== orderB ? orderA - orderB : a.label.localeCompare(b.label, 'fr');
        });
}

const FIELD_LABEL_KEYS: Record<string, string> = {
    PERSON_ENTITY: 'stores',
    CUSTOMER_IDENTIFIER: 'identifiers',
    CUSTOMER_ACTIVATION_DATE: 'cardOpening',
    CUSTOMER_SUBSCRIPTIONS_TYPE: 'subscriptionsType',
    CUSTOMER_ACCOUNT: 'customerAccount',
    CUSTOMER_ACCOUNT_TYPE: 'accountType',
    CUSTOMER_SCORE: 'customerScore',
    CUSTOMER_SCORE_BEST: 'bestCustomers',
    TRANSACTION_NB: 'purchaseCount',
    TRANSACTION_PERIOD: 'purchasePeriod',
    CUSTOMER_OPTIN_EMAIL: 'optinEmail',
    CUSTOMER_STATUS_EMAIL: 'statusEmail',
    CUSTOMER_OPTIN_SMS: 'optinSms',
    CUSTOMER_STATUS_SMS: 'statusSms',
    CUSTOMER_OPTIN_COURRIER: 'optinCourrier',
    CUSTOMER_STATUS_COURRIER: 'statusCourrier',
    CUSTOMER_CITY: 'city',
    CUSTOMER_DEPARTMENT: 'department',
    CUSTOMER_COUNTRY: 'country',
    CUSTOMER_LIST_BY_IDENTIFIER: 'listByIdentifier',
    CUSTOMER_LIST_BY_CARTE: 'listByCard',
};

export const resolveFieldLabel = (field: QueryField, t: Translate): string => {
    const key = FIELD_LABEL_KEYS[field.name];
    return key ? t(`pages.clients.queryBuilder.simple.fieldLabels.${key}`) : field.label;
};

const SUBCATEGORIES: { titleKey: string; fieldNames: string[] }[] = [
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.clientCategory',
        fieldNames: ['PERSON_ENTITY', 'CUSTOMER_IDENTIFIER', 'CUSTOMER_ACTIVATION_DATE', 'CUSTOMER_SUBSCRIPTIONS_TYPE', 'CUSTOMER_ACCOUNT', 'CUSTOMER_ACCOUNT_TYPE'],
    },
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.clientScore',
        fieldNames: ['CUSTOMER_SCORE', 'CUSTOMER_SCORE_BEST'],
    },
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.purchaseBehavior',
        fieldNames: ['TRANSACTION_NB', 'TRANSACTION_PERIOD'],
    },
    {
        titleKey: GDPR_CATEGORY_TITLE_KEY,
        fieldNames: [
            'CUSTOMER_OPTIN_EMAIL', 'CUSTOMER_STATUS_EMAIL',
            'CUSTOMER_OPTIN_SMS', 'CUSTOMER_STATUS_SMS',
            'CUSTOMER_OPTIN_COURRIER', 'CUSTOMER_STATUS_COURRIER',
        ],
    },
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.geography',
        fieldNames: ['CUSTOMER_CITY', 'CUSTOMER_DEPARTMENT', COUNTRY_FIELD_NAME],
    },
    {
        titleKey: CLIENT_LISTS_CATEGORY_TITLE_KEY,
        fieldNames: ['CUSTOMER_LIST_BY_IDENTIFIER', 'CUSTOMER_LIST_BY_CARTE'],
    },
];

const FIELD_NAME_TO_CATEGORY_KEY = new Map<string, string>();
const FIELD_NAME_TO_ORDER = new Map<string, number>();
SUBCATEGORIES.forEach(({ titleKey, fieldNames }) => {
    fieldNames.forEach((name, index) => {
        FIELD_NAME_TO_CATEGORY_KEY.set(name, titleKey);
        FIELD_NAME_TO_ORDER.set(name, index);
    });
});

interface FieldSubcategory {
    titleKey: string;
    fields: QueryField[];
}

export function groupFieldsBySubcategory(fields: QueryField[]): FieldSubcategory[] {
    const order = [...SUBCATEGORIES.map(s => s.titleKey), OTHER_CATEGORY_KEY];
    const buckets = new Map<string, QueryField[]>(order.map(titleKey => [titleKey, []]));

    fields.forEach(field => {
        const titleKey = FIELD_NAME_TO_CATEGORY_KEY.get(field.name) ?? OTHER_CATEGORY_KEY;
        buckets.get(titleKey)!.push(field);
    });

    buckets.forEach(bucketFields => {
        bucketFields.sort((a, b) => (FIELD_NAME_TO_ORDER.get(a.name) ?? 0) - (FIELD_NAME_TO_ORDER.get(b.name) ?? 0));
    });

    return order
        .map(titleKey => ({ titleKey, fields: buckets.get(titleKey) ?? [] }))
        .filter(group => group.fields.length > 0);
}

export const toHelpKey = (titleKey: string): string => titleKey.replace('.categories.', '.categoryHelp.');
